import io from "socket.io-client";
import { API_BASE_URL } from "../../config/apiConfig";
import { getAuthToken } from "../../utils/apiUtils";

/**
 * Socket.IO service for Research Rooms real-time messaging
 * Handles connections to the /research-rooms namespace
 */
class RoomSocketService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.authenticated = false;
    this.currentRoomId = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    // Pending room join queue - for rooms requested before auth completes
    this.pendingRoomJoin = null;
    // Connection promise for waiting on connection
    this.connectionPromise = null;
    this.connectionResolve = null;
  }

  /**
   * Initialize socket connection to research rooms namespace
   */
  connect(token) {
    if (this.socket && this.connected) {
return this.socket;
    }

    try {
      const authToken = token || getAuthToken();
      if (!authToken) {
return null;
      }

      // Validate API_BASE_URL first
      if (!API_BASE_URL) {
throw new Error("API_BASE_URL not configured");
      }

      // Connect to research-rooms namespace (remove trailing /api if present)
      let baseUrl = API_BASE_URL.endsWith("/api")
        ? API_BASE_URL.slice(0, -4)
        : API_BASE_URL;

      // Ensure we have a valid URL with protocol AFTER removing /api
      if (!baseUrl.startsWith("http://") && !baseUrl.startsWith("https://")) {
throw new Error("Invalid API URL configuration - missing protocol");
      }

      const socketUrl = `${baseUrl}/research-rooms`;

      // Final validation: make sure URL doesn't have weird patterns
      if (
        socketUrl.includes("https://https") ||
        socketUrl.includes("http://http")
      ) {
throw new Error("Malformed Socket URL - double protocol detected");
      }
this.socket = io(socketUrl, {
        path: "/socket.io/",
        auth: { token: authToken },
        transports: ["polling", "websocket"],
        withCredentials: true,
        timeout: 20000,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: this.maxReconnectAttempts,
      });

      // Add raw socket.io event listeners for debugging
      this.socket.onAny((eventName, ...args) => {
});

      this.setupEventListeners();
      return this.socket;
    } catch (error) {
return null;
    }
  }

  /**
   * Extract user ID from JWT token
   */
  extractUserId(token) {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.id || payload.userId || payload._id;
    } catch (e) {
return null;
    }
  }

  /**
   * Set up socket event listeners
   */
  setupEventListeners() {
    if (!this.socket) return;

    // Connection events
    this.socket.on("connect", () => {
this.connected = true;
      this.reconnectAttempts = 0;

      // Authenticate
      const token = getAuthToken();
      if (token) {
        this.socket.emit("authenticate", token);

        // Extract user ID and auto-join user room (CRITICAL for receiving messages!)
        const userId = this.extractUserId(token);
        if (userId) {
this.socket.emit("join_user_room", { userId }, (response) => {
});
        } else {
}
      }

      this.emit("socket_connected");
    });

    this.socket.on("auth_success", (data) => {
this.authenticated = true;

      // Resolve connection promise if waiting
      if (this.connectionResolve) {
        this.connectionResolve(true);
        this.connectionResolve = null;
        this.connectionPromise = null;
      }

      this.emit("socket_authenticated", data);

      // Join personal notification channel for receiving notifications
      const token = getAuthToken();
      if (token) {
        const userId = this.extractUserId(token);
        if (userId) {
          this.socket.emit("join_user_channel", userId);
        }
      }

      // Process pending room join if any
      if (this.pendingRoomJoin) {
        const roomId = this.pendingRoomJoin;
        this.pendingRoomJoin = null;
        this.joinRoom(roomId);
      }
      // Also rejoin current room if we were in one (reconnection case)
      else if (this.currentRoomId) {
this.joinRoom(this.currentRoomId);
      }
    });

    this.socket.on("auth_error", (data) => {
this.authenticated = false;
      this.emit("socket_auth_error", data);
    });

    this.socket.on("disconnect", (reason) => {
this.connected = false;
      this.authenticated = false;
      this.emit("socket_disconnected", reason);
    });

    // Listen for transport upgrades
    this.socket.io.engine.on("upgrade", (transport) => {
});

    this.socket.on("connect_error", (error) => {
this.reconnectAttempts++;
      this.emit("socket_error", error);
    });

    // Room events
    this.socket.on("room_joined", (data) => {
this.currentRoomId = data.roomId;
      this.emit("room_joined", data);
    });

    this.socket.on("room_left", (data) => {
if (this.currentRoomId === data.roomId) {
        this.currentRoomId = null;
      }
      this.emit("room_left", data);
    });

    // Message events
    this.socket.on("new_message", (message) => {
this.emit("new_message", message);
    });

    this.socket.on("message_sent", (response) => {
this.emit("message_sent", response);
    });

    this.socket.on("message_edited", (data) => {
this.emit("message_edited", data);
    });

    this.socket.on("message_deleted", (data) => {
this.emit("message_deleted", data);
    });

    // Reaction events
    this.socket.on("reaction_added", (data) => {
this.emit("reaction_added", data);
    });

    this.socket.on("reaction_removed", (data) => {
this.emit("reaction_removed", data);
    });

    // Pin events
    this.socket.on("message_pinned", (data) => {
this.emit("message_pinned", data);
    });

    this.socket.on("message_unpinned", (data) => {
this.emit("message_unpinned", data);
    });

    // Typing events
    this.socket.on("user_typing", (data) => {
this.emit("user_typing", data);
    });

    this.socket.on("user_stop_typing", (data) => {
this.emit("user_stop_typing", data);
    });

    // Presence events
    this.socket.on("user_online", (data) => {
this.emit("user_online", data);
    });

    this.socket.on("user_offline", (data) => {
this.emit("user_offline", data);
    });

    // Read receipt events
    this.socket.on("message_read", (data) => {
this.emit("message_read", data);
    });

    // File events
    this.socket.on("new_file_uploaded", (data) => {
this.emit("new_file_uploaded", data);
    });

    this.socket.on("file_deleted", (data) => {
this.emit("file_deleted", data);
    });

    // Resource events
    this.socket.on("resource_created", (data) => {
this.emit("resource_created", data);
    });

    this.socket.on("resource_updated", (data) => {
this.emit("resource_updated", data);
    });

    this.socket.on("resource_deleted", (data) => {
this.emit("resource_deleted", data);
    });

    // Task events (Phase 4)
    this.socket.on("task_created", (data) => {
this.emit("task_created", data);
    });

    this.socket.on("task_updated", (data) => {
this.emit("task_updated", data);
    });

    this.socket.on("task_status_changed", (data) => {
this.emit("task_status_changed", data);
    });

    this.socket.on("task_deleted", (data) => {
this.emit("task_deleted", data);
    });

    this.socket.on("task_comment_added", (data) => {
this.emit("task_comment_added", data);
    });

    this.socket.on("subtask_toggled", (data) => {
this.emit("subtask_toggled", data);
    });

    // Calendar events (Phase 5)
    this.socket.on("new_event_created", (data) => {
this.emit("new_event_created", data);
    });

    this.socket.on("event_updated", (data) => {
this.emit("event_updated", data);
    });

    this.socket.on("event_rsvp_updated", (data) => {
this.emit("event_rsvp_updated", data);
    });

    this.socket.on("event_cancelled", (data) => {
this.emit("event_cancelled", data);
    });

    // ========== ROOM NOTIFICATION EVENTS (Real-time) ==========
    
    // Personal notification channel confirmation
    this.socket.on("user_channel_joined", (data) => {
      this.emit("user_channel_joined", data);
    });

    // Personal notifications (sent to user's personal channel)
    this.socket.on("notification", (notification) => {
      this.emit("notification", notification);
    });

    // Join Request Notifications
    this.socket.on("join_request_accepted", (data) => {
      this.emit("join_request_accepted", data);
    });

    // Member Management Events (broadcast to room)
    this.socket.on("member_removed", (data) => {
      this.emit("member_removed", data);
    });

    this.socket.on("member_left", (data) => {
      this.emit("member_left", data);
    });

    this.socket.on("member_joined", (data) => {
      this.emit("member_joined", data);
    });

    // Room Lifecycle Events
    this.socket.on("room_deleted", (data) => {
      this.emit("room_deleted", data);
    });

    this.socket.on("room_updated", (data) => {
      this.emit("room_updated", data);
    });

    // Calendar Notification Events
    this.socket.on("new_calendar_event", (data) => {
      this.emit("new_calendar_event", data);
    });

    this.socket.on("calendar_event_updated", (data) => {
      this.emit("calendar_event_updated", data);
    });

    this.socket.on("calendar_event_deleted", (data) => {
      this.emit("calendar_event_deleted", data);
    });

    // Meeting Reminder (sent by cron job)
    this.socket.on("event_starting_soon", (data) => {
      this.emit("event_starting_soon", data);
    });

    // Error events
    this.socket.on("error", (error) => {
this.emit("socket_error", error);
    });
  }

  /**
   * Join a research room
   * If socket isn't ready, queues the join for when auth completes
   */
  joinRoom(roomId) {
    // Always track the room we want to be in
    this.currentRoomId = roomId;

    if (!this.connected || !this.authenticated) {
this.pendingRoomJoin = roomId;
      return false;
    }
this.socket.emit("join_room", roomId);
    this.pendingRoomJoin = null; // Clear pending since we're joining now
    return true;
  }

  /**
   * Wait for socket to be authenticated
   * Returns a promise that resolves when authenticated or rejects on timeout
   */
  waitForAuth(timeoutMs = 10000) {
    if (this.authenticated) {
      return Promise.resolve(true);
    }

    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    this.connectionPromise = new Promise((resolve, reject) => {
      this.connectionResolve = resolve;

      // Timeout after specified duration
      setTimeout(() => {
        if (!this.authenticated) {
          this.connectionResolve = null;
          this.connectionPromise = null;
          reject(new Error("Socket authentication timeout"));
        }
      }, timeoutMs);
    });

    return this.connectionPromise;
  }

  /**
   * Check if socket is ready for operations
   */
  isReady() {
    return this.connected && this.authenticated;
  }

  /**
   * Leave a research room
   */
  leaveRoom(roomId) {
    if (!this.connected || !roomId) return;
this.socket.emit("leave_room", roomId);

    if (this.currentRoomId === roomId) {
      this.currentRoomId = null;
    }
  }

  /**
   * Send a message via socket
   */
  sendMessage(roomId, content, replyToId = null, callback) {
    if (!this.connected || !this.authenticated) {
if (callback) callback({ success: false, message: "Not connected" });
      return;
    }

    const payload = {
      content,
      roomId,
      replyToId,
      broadcast: false,
    };
this.socket.emit("send_message", payload, (response) => {
if (callback) callback(response);
    });
  }

  /**
   * Edit a message
   */
  editMessage(roomId, messageId, newContent, callback) {
    if (!this.connected || !this.authenticated) {
      if (callback) callback({ success: false, message: "Not connected" });
      return;
    }

    this.socket.emit(
      "edit_message",
      { roomId, messageId, content: newContent },
      callback
    );
  }

  /**
   * Delete a message
   */
  deleteMessage(roomId, messageId, callback) {
    if (!this.connected || !this.authenticated) {
      if (callback) callback({ success: false, message: "Not connected" });
      return;
    }

    this.socket.emit("delete_message", { roomId, messageId }, callback);
  }

  /**
   * Send typing indicator
   */
  startTyping(roomId) {
    if (!this.connected || !this.authenticated) return;
    this.socket.emit("typing", roomId);
  }

  /**
   * Stop typing indicator
   */
  stopTyping(roomId) {
    if (!this.connected || !this.authenticated) return;
    this.socket.emit("stop_typing", roomId);
  }

  /**
   * Mark messages as read
   */
  markAsRead(roomId, messageIds) {
    if (!this.connected || !this.authenticated) return;
    this.socket.emit("mark_read", { roomId, messageIds });
  }

  /**
   * Notify file uploaded
   */
  notifyFileUploaded(roomId, attachment) {
    if (!this.connected || !this.authenticated) return;
    this.socket.emit("file_uploaded", { roomId, attachment });
  }

  /**
   * Notify resource created
   */
  notifyResourceCreated(roomId, resource) {
    if (!this.connected || !this.authenticated) return;
    this.socket.emit("resource_created", { roomId, resource });
  }

  /**
   * Notify task created (Phase 4)
   */
  notifyTaskCreated(roomId, task) {
    if (!this.connected || !this.authenticated) return;
    this.socket.emit("task_created", { roomId, task });
  }

  /**
   * Notify task updated (Phase 4)
   */
  notifyTaskUpdated(roomId, taskId, updates) {
    if (!this.connected || !this.authenticated) return;
    this.socket.emit("task_updated", { roomId, taskId, updates });
  }

  /**
   * Notify task status changed (Phase 4)
   */
  notifyTaskStatusChanged(roomId, taskId, status) {
    if (!this.connected || !this.authenticated) return;
    this.socket.emit("task_status_changed", { roomId, taskId, status });
  }

  /**
   * Notify event created (Phase 5)
   */
  notifyEventCreated(roomId, event) {
    if (!this.connected || !this.authenticated) return;
    this.socket.emit("event_created", { roomId, event });
  }

  /**
   * Notify event updated (Phase 5)
   */
  notifyEventUpdated(roomId, eventId, updates) {
    if (!this.connected || !this.authenticated) return;
    this.socket.emit("event_updated", { roomId, eventId, updates });
  }

  /**
   * Notify RSVP updated (Phase 5)
   */
  notifyRSVPUpdated(roomId, eventId, rsvp) {
    if (!this.connected || !this.authenticated) return;
    this.socket.emit("rsvp_updated", { roomId, eventId, rsvp });
  }

  /**
   * Event listener management
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);

    // Return unsubscribe function
    return () => this.off(event, callback);
  }

  off(event, callback) {
    if (!this.listeners.has(event)) return;

    const eventListeners = this.listeners.get(event);
    const index = eventListeners.indexOf(callback);
    if (index > -1) {
      eventListeners.splice(index, 1);
    }
  }

  emit(event, data) {
    const eventListeners = this.listeners.get(event) || [];
    eventListeners.forEach((callback) => {
      try {
        callback(data);
      } catch (error) { /* ignored */ }
    });
  }

  removeAllListeners(event) {
    if (event) {
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
    }
  }

  /**
   * Utility methods
   */
  isConnected() {
    return this.connected && this.socket?.connected;
  }

  isAuthenticated() {
    return this.authenticated;
  }

  getCurrentRoom() {
    return this.currentRoomId;
  }

  getSocket() {
    return this.socket;
  }

  disconnect() {
    if (this.socket) {
// Leave current room before disconnect
      if (this.currentRoomId) {
        this.leaveRoom(this.currentRoomId);
      }

      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
      this.authenticated = false;
      this.currentRoomId = null;
      this.removeAllListeners();
    }
  }
}

// Create singleton instance
const roomSocketService = new RoomSocketService();

export default roomSocketService;
